import toPairs from 'ramda/es/toPairs';
import values from 'ramda/es/values';
import { Reducer } from 'redux';
import { mapDispatch } from '../../map/map';
import { addLayersToMap, toggleMapLayers } from '../../map/mapActions';
import {Layer, isVisible, LayerGroup, BgLayerSubGroup} from '../../model/layer/layer';
import { toLayerCollection } from './actions';
import {
    CLEAR_SELECTED,
    HoveredStedfestingAction,
    HOVER_STEDFESTING,
    INIT_LAYERS,
    LayerAction,
    LayerGroupToggleAction,
    MapState,
    SelectedStedfestingAction,
    SELECT_STEDFESTING,
    SET_STEDFESTING,
    StedfestingAction,
    TOGGLE_LAYER_GROUP,
    KARTLAGFEILER,
    SetKartlagFeilerAction,
    TURN_OFF_BG_LAYERS,
} from './types';
import {curry, identity} from 'ramda';

const initialState: MapState = {
    layers: {},
    stedfesting: [],
    selectedStedfesting: [],
    hoveredStedfesting: null,
    errorLoadingKartlag: ""
};

const mapLayers = localStorage.mapLayers || [];
let storedLayers: string[] = [];
try {
    storedLayers = JSON.parse(mapLayers);
} catch (e) {
    localStorage.clear();
}
const layerInSelectedLayersGroup = (selectedArray: Layer[], layer: Layer): boolean =>
    selectedArray[0].group && layer.group && selectedArray[0].group === layer.group;

const toggleLayerVisibility = (layer: Layer): void =>
    (layer.options = {
        ...layer.options,
        visible: !layer.options.visible,
    });
const disableLayerVisibility = (layer: Layer): Layer => {
    return {
        ...layer,
        options: {
            ...layer.options,
            visible: false,
        },
    };
};

export const mapReducer: Reducer<MapState> = (state = initialState, action): MapState => {
    switch (action.type) {
        case INIT_LAYERS: {
            const { layers } = action as LayerAction;
            const bgLayers = values(layers).filter(layer => layer.group === LayerGroup.BG);
            const otherLayers = values(layers).filter(layer => layer.group !== LayerGroup.BG);
            // Each background layer subgroup should have two layers when no map providers are turned off
            const isAnyMapProvidersTurnedOff = bgLayers.filter(layer => layer.subGroup === BgLayerSubGroup.graatone).length < 2;
            const bgLayersInStore = storedLayers.filter(id => values(BgLayerSubGroup).includes(id as BgLayerSubGroup));  
            // If all map-providers are available, we want to filter out the 'basis' option
            const filteredStoredBgLayers = isAnyMapProvidersTurnedOff ? bgLayersInStore : bgLayersInStore.filter(id => id !== BgLayerSubGroup.basis);
            
            const bgLayersArray = bgLayers.map(layer => ({
                ...layer,
                options: {
                    ...layer.options,
                    visible: filteredStoredBgLayers.length > 0 ? filteredStoredBgLayers.includes(layer.subGroup) : layer.options.visible,
                }
            }));
            
            const otherLayersArray: Layer[] = otherLayers.map((layer) => ({
                ...layer,
                options: {
                    ...layer.options,
                    visible: storedLayers.includes(layer.id),
                },
            }));
            
            const layersArray = [...bgLayersArray, ...otherLayersArray];
            mapDispatch(addLayersToMap(layersArray));
            const layerCollection = toLayerCollection(layersArray);

            return {
                ...state,
                layers: layerCollection,
            };
        }
        case TOGGLE_LAYER_GROUP: {
            const { layers, active } = action as LayerGroupToggleAction;

            const setVisible = curry(
                (visible: boolean, layer: Layer): Layer => ({
                    ...layer,
                    options: {
                        ...layer.options,
                        visible,
                    },
                })
            );

            const hasGroup = values(layers).some(layer => layer.group in LayerGroup);

            const groups = values(layers).map(layer => layer.group).filter(identity);
            
            // Finner alle kartlag i samme gruppe som det/de kartlag som skal endres
            const layersInGroup = hasGroup
                ? toLayerCollection(
                      values(state.layers)
                          .filter(layer => groups.includes(layer.group))
                          .map(setVisible(false))
                  )
                : {};
            // Alle endrede kartlag oppdateres med riktig visible-verdi
            const changedLayers = toLayerCollection(values(layers).map<Layer>(setVisible(active)));

            // Oppretter ny MapState
            const newState: MapState = {
                ...state,
                layers: {
                    ...state.layers,
                    ...layersInGroup,
                    ...changedLayers,
                },
            };

            // Varsler kartet om hvilke kartlag som nå skal være aktiv
            const visibleLayerIds = values(newState.layers)
                .filter(isVisible)
                .map(layer => layer.id);
            mapDispatch(toggleMapLayers(visibleLayerIds));

            return newState;
        }
        
        // Slår av alle bakgrunnskartlag - men OSM er alltid på
        case TURN_OFF_BG_LAYERS: {
            const backgroundLayers = toLayerCollection(
                    values(state.layers)
                        .filter(layer =>   layer.group === LayerGroup.BG)
                        .map(disableLayerVisibility));

            const newState: MapState = {
                ...state,
                layers: {
                    ...state.layers,
                    ...backgroundLayers,
                },
            };

            // Finner de kartlagene som var på fra før (feks annet enn bakgrunnskart)
            const visibleLayerIds = values(newState.layers)
                .filter(isVisible)
                .map(layer => layer.id);
            mapDispatch(toggleMapLayers(visibleLayerIds));

            return newState;
        }

        case SET_STEDFESTING: {
            const { stedfestings } = action as StedfestingAction;
            return {
                ...state,
                stedfesting: stedfestings,
            };
        }

        case SELECT_STEDFESTING: {
            const { selectedStedfesting } = action as SelectedStedfestingAction;
            return {
                ...state,
                selectedStedfesting: selectedStedfesting,
            };
        }

        case HOVER_STEDFESTING: {
            const { hoveredStedfesting } = action as HoveredStedfestingAction;
            return {
                ...state,
                hoveredStedfesting: hoveredStedfesting,
            };
        }

        case CLEAR_SELECTED: {
            return {
                ...state,
                selectedStedfesting: [],
            };
        }
        
        case KARTLAGFEILER: {
            const { kartlagnavn } = action as SetKartlagFeilerAction;
            return {
                ...state,
                errorLoadingKartlag: kartlagnavn
            }
        }
        
        default: {
            return state;
        }
    }
};
